import React, { useState } from "react";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Separator } from "../ui/separator";
import { Badge } from "../ui/badge";
import { Database, Flag, Plus, Trash2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import type { ResourceType } from "../../types/subscription";

type FeaturesEditorProps = {
  // Flat format: { "altzero_final_policies": 1, "altzero_ai_tokens": 5000, "altzero_advanced": false }
  features: Record<string, unknown>;
  onChange: (features: Record<string, unknown>) => void;
  // Loaded from base_resource_types — drives the quota rows
  resourceTypes: ResourceType[];
  disabled?: boolean;
};

const formatLabel = (code: string): string =>
  code.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const FeaturesEditor: React.FC<FeaturesEditorProps> = ({
  features,
  onChange,
  resourceTypes,
  disabled = false,
}) => {
  const [newFlagName, setNewFlagName] = useState("");

  const resourceTypeCodes = new Set(resourceTypes.map((rt) => rt.code));

  // Boolean keys that are not resource type codes are feature flags
  const flagKeys = Object.keys(features).filter(
    (k) => typeof features[k] === "boolean" && !resourceTypeCodes.has(k)
  );

  const setFeature = (key: string, value: unknown) =>
    onChange({ ...features, [key]: value });

  const removeFeature = (key: string) => {
    const updated = { ...features };
    delete updated[key];
    onChange(updated);
  };

  const addFlag = () => {
    const key = newFlagName.trim().toLowerCase().replace(/\s+/g, "_");
    if (!key || key in features) return;
    onChange({ ...features, [key]: false });
    setNewFlagName("");
  };

  return (
    <div className="space-y-6">
      {/* ── Resource Quotas ── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Database className="h-4 w-4 text-emerald-500" />
            Resource Quotas
            <Badge variant="outline" className="text-xs font-normal">
              from base_resource_types
            </Badge>
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Set the limit for each resource. Use <strong>-1</strong> for unlimited, <strong>0</strong> to block.
            New resource types are added via the DB (<code>base_resource_types</code>).
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {resourceTypes.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No resource types registered. Run migration 005 to seed them.
            </p>
          )}
          {resourceTypes.map((rt, idx) => {
            const val = features[rt.code];
            return (
              <React.Fragment key={rt.code}>
                {idx > 0 && <Separator />}
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{rt.name}</span>
                      <code className="text-xs text-muted-foreground bg-muted px-1 rounded">
                        {rt.code}
                      </code>
                    </div>
                    {rt.description && (
                      <p className="text-xs text-muted-foreground mt-0.5">{rt.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {(!rt.value_type || rt.value_type === "count") && (() => {
                      const numVal = typeof val === "number" ? val : 0;
                      return (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Input
                                type="number"
                                min="-1"
                                value={numVal}
                                onChange={(e) => {
                                  const v = e.target.value === "" ? 0 : parseInt(e.target.value);
                                  setFeature(rt.code, isNaN(v) ? 0 : v);
                                }}
                                className="w-24 text-center"
                                disabled={disabled}
                              />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>-1 = unlimited · 0 = blocked · positive = limit</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })()}
                    {rt.value_type === "flag" && (
                      <Switch
                        checked={!!val}
                        onCheckedChange={(checked) => setFeature(rt.code, checked)}
                        disabled={disabled}
                      />
                    )}
                    {rt.value_type === "link" && (
                      <Input
                        type="url"
                        placeholder="https://..."
                        value={typeof val === "string" ? val : ""}
                        onChange={(e) => setFeature(rt.code, e.target.value)}
                        className="w-48"
                        disabled={disabled}
                      />
                    )}
                    {rt.value_type === "plugin" && (
                      <Input
                        placeholder="plugin-id"
                        value={typeof val === "string" ? val : ""}
                        onChange={(e) => setFeature(rt.code, e.target.value)}
                        className="w-36"
                        disabled={disabled}
                      />
                    )}
                    {(!rt.value_type || rt.value_type === "count") && (
                      <span className="text-xs text-muted-foreground w-16 truncate">
                        {typeof val === "number" && val === -1 ? "∞ unlimited" : rt.unit_label}
                      </span>
                    )}
                    {rt.value_type === "flag" && (
                      <span className="text-xs text-muted-foreground w-14">
                        {val ? "Enabled" : "Disabled"}
                      </span>
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </CardContent>
      </Card>

      {/* ── Feature Flags ── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Flag className="h-4 w-4 text-blue-500" />
            Feature Flags
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Boolean on/off switches (e.g. <code>altzero_advanced</code>, <code>custom_branding</code>).
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {flagKeys.length === 0 && (
            <p className="text-sm text-muted-foreground">No feature flags configured.</p>
          )}
          {flagKeys.map((key, idx) => (
            <React.Fragment key={key}>
              {idx > 0 && <Separator />}
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <code className="text-sm bg-muted px-1.5 py-0.5 rounded">{key}</code>
                  <span className="text-xs text-muted-foreground">{formatLabel(key)}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Switch
                    checked={!!features[key]}
                    onCheckedChange={(checked) => setFeature(key, checked)}
                    disabled={disabled}
                  />
                  <span className="text-xs text-muted-foreground w-14">
                    {features[key] ? "Enabled" : "Disabled"}
                  </span>
                  {!disabled && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFeature(key)}
                      className="text-red-400 hover:text-red-600 h-8 w-8 p-0"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            </React.Fragment>
          ))}
          {!disabled && (
            <>
              {flagKeys.length > 0 && <Separator />}
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Flag name e.g. altzero_advanced"
                  value={newFlagName}
                  onChange={(e) => setNewFlagName(e.target.value)}
                  className="flex-1"
                  onKeyDown={(e) => e.key === "Enter" && addFlag()}
                />
                <Button variant="outline" size="sm" onClick={addFlag} disabled={!newFlagName.trim()}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Flag
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

    </div>
  );
};
